// Rail areas (founder-ratified shell IA, 2026-07-07; grouped rail ratified
// 2026-07-31; shell v2 scope model docs/09 §4). Transcripts and Markers are
// top-level, agent-scoped resources; the Agent hub is opened from the agent
// selector, and `/agents` is the agents index (palette/direct URL, not a rail
// item). Templates folded into `/simulations?tab=templates` and Scenarios was
// promoted to the top level by the 2026-07-31 ratification, so neither is an
// area of its own any more (`templates` is a Simulations tab).
// The working scope rides root-owned search params. A selected-agent URL is
// canonical with `?agent=` only; `?team=` is preserved for All agents and
// team-bound settings screens.
import type { WorkingScopeSearch } from "./workingScope";

export type ShellArea =
  | "agent"
  | "agents"
  | "alerts"
  | "dashboard"
  | "markers"
  | "personas"
  | "review"
  | "scenarios"
  | "simulations"
  | "transcripts"
  | "settings";
export type ShellSettingsTab =
  | "agent"
  | "agents"
  | "api-keys"
  | "audit-log"
  | "billing"
  | "invitations"
  | "license"
  | "members"
  | "organization"
  | "phone-numbers"
  | "profile"
  | "retention"
  | "teams"
  | "usage";

export const agentScopeWiredAreas = [
  "agent",
  "agents",
  "alerts",
  "dashboard",
  "markers",
  "personas",
  "review",
  "scenarios",
  "simulations",
  "transcripts",
] as const satisfies readonly ShellArea[];

const agentScopeWiredAreaSet = new Set<ShellArea>(agentScopeWiredAreas);
const teamBoundScopeRoutes = new Set<ShellNavRoute>([
  "/settings/invitations",
  "/settings/members",
]);

// Concrete route paths the shell navigates to from chrome. Kept as a literal
// union so `navigate({ to })` can be derived straight from the registry without
// a per-route switch.
export type ShellNavRoute =
  | "/agent"
  | "/agents"
  | "/alerts"
  | "/dashboard"
  | "/markers"
  | "/personas"
  | "/review"
  | "/scenarios"
  | "/simulations"
  | "/simulations/new"
  | "/transcripts"
  | "/settings"
  | "/settings/agent"
  | "/settings/agents"
  | "/settings/api-keys"
  | "/settings/audit-log"
  | "/settings/billing"
  | "/settings/invitations"
  | "/settings/license"
  | "/settings/members"
  | "/settings/organization"
  | "/settings/phone-numbers"
  | "/settings/profile"
  | "/settings/retention"
  | "/settings/teams"
  | "/settings/usage";

// Grouping used by the settings-shell sidebar section headers. `primary`
// entries are the top-level areas the icon rail renders — the rail renderer
// renders ONLY `primary`, and its order matches the rail's own order
// (SidebarNav groups the same list under section headings). `index` entries
// are palette/direct-URL destinations that never join the rail (`/agents`;
// Templates, now a `/simulations` tab — docs/09 §4). The
// `settings-*` groups are the settings shell's headed sections (docs/09 §4):
// Organization, Team, Agent, Personal.
export type ShellNavGroup =
  | "index"
  | "primary"
  | "settings-agent"
  | "settings-organization"
  | "settings-personal"
  | "settings-team";

// Screen-owned search a registry destination carries into its target screen.
// The palette keeps Templates within Simulations. Chrome never invents other
// screen params — they stay owned by the screen (shellNavigationSearch §2).
export interface ShellNavScreenSearch {
  tab: "templates";
}

export interface ShellNavEntry {
  group: ShellNavGroup;
  // A hosted-only destination (credit billing): the row and its palette entry
  // surface only when GET /v1/me reports `credit_billing_enabled` (hosted
  // installs). Self-managed installs never see it.
  hostedOnly?: boolean;
  id: string;
  // A destination that exists only on a LICENSED install: the row and its
  // palette entry surface only where GET /v1/system/license reports a state
  // other than `not_required`. Hosted installs carry no license, so the
  // screen would be a permanently empty "Not required" page — dead chrome.
  // Gated on the license API's own answer rather than inferred from the
  // hosted billing flag: the licensing surface is the authority on whether
  // this install is licensed.
  licensedOnly?: boolean;
  // A destination that exists only where Marker can reach a carrier: the row
  // and its palette entry surface only when GET /v1/me reports
  // `number_provisioning_enabled`. Self-managed installs populate the pool
  // from customer-private SIP inventory and never see it.
  numberProvisioningOnly?: boolean;
  label: string;
  // Screen params appended to the destination's canonical scope search.
  search?: ShellNavScreenSearch;
  // The row label inside the settings-shell sidebar when it differs from the
  // palette label (e.g. "General" under the Organization / Agent headers —
  // the group heading supplies the scope, so the row stays short while the
  // palette label stays globally unambiguous).
  settingsRowLabel?: string;
  settingsTab?: ShellSettingsTab;
  // A destination whose screen is not built yet. The command palette includes
  // live routes only, so it excludes every Soon entry; the settings panel
  // renders one as a disabled `isSoon` row.
  soon?: boolean;
  to?: ShellNavRoute;
}

// Single source of truth for the shell's route-bearing navigation. Both the
// command palette and organization menu derive from this list, so a settings
// screen can never be live in chrome yet stale or absent in the palette.
// `/settings/identity` is intentionally omitted. Deferred simulated surfaces
// carry no route here and are not palette destinations.
export const shellNavRegistry: readonly ShellNavEntry[] = [
  // The agents index: a command-palette/direct-URL destination, NOT a rail
  // item (docs/09 §4).
  { group: "index", id: "agents", label: "Agents", to: "/agents" },
  // Templates: the same palette-only mechanism. The Templates rail item was
  // removed by the 2026-07-31 ratification — templates are a tab on the
  // Simulations screen — but the palette keeps the noun as a destination.
  {
    group: "index",
    id: "templates",
    label: "Templates",
    search: { tab: "templates" },
    to: "/simulations",
  },
  // Simulations leads the rail (founder-ratified 2026-07-26): the
  // launch/orchestration hub over /v1/batches. `batch` stays the domain noun;
  // results remain in Transcripts (docs/09 §4).
  {
    group: "primary",
    id: "simulations",
    label: "Simulations",
    to: "/simulations",
  },
  {
    group: "primary",
    id: "transcripts",
    label: "Transcripts",
    to: "/transcripts",
  },
  // Library group (rail order, 2026-07-31): Markers, Personas, Scenarios.
  { group: "primary", id: "markers", label: "Markers", to: "/markers" },
  { group: "primary", id: "personas", label: "Personas", to: "/personas" },
  { group: "primary", id: "scenarios", label: "Scenarios", to: "/scenarios" },
  // Quality group: marker-first Review.
  { group: "primary", id: "review", label: "Review", to: "/review" },
  // Monitor group: Dashboard, Alerts.
  { group: "primary", id: "dashboard", label: "Dashboard", to: "/dashboard" },
  { group: "primary", id: "alerts", label: "Alerts", to: "/alerts" },
  // Settings is the LAST rail item (docs/09 §4: the rail ends on
  // Settings, outside every group). `/settings` is the settings-shell front
  // door — its index route redirects to the agent or organization row
  // (settingsIndexTarget).
  { group: "primary", id: "settings", label: "Settings", to: "/settings" },
  {
    group: "settings-organization",
    id: "organization",
    label: "Organization profile",
    settingsRowLabel: "General",
    settingsTab: "organization",
    to: "/settings/organization",
  },
  {
    group: "settings-organization",
    id: "teams",
    label: "Teams",
    settingsTab: "teams",
    to: "/settings/teams",
  },
  {
    group: "settings-team",
    id: "members",
    label: "Members & roles",
    settingsTab: "members",
    to: "/settings/members",
  },
  {
    group: "settings-team",
    id: "invitations",
    label: "Invitations",
    settingsTab: "invitations",
    to: "/settings/invitations",
  },
  // The Team group's archive-lifecycle row (docs/09 §4): restore an archived
  // agent, or find the support path for permanent deletion.
  {
    group: "settings-team",
    id: "team-agents",
    // Palette label is disambiguated from the `/agents` index entry, the same
    // way the Agent group's row avoids colliding with Organization General;
    // the settings sidebar still shows a bare "Agents" under its Team header.
    label: "Archived agents",
    settingsRowLabel: "Agents",
    settingsTab: "agents",
    to: "/settings/agents",
  },
  {
    group: "settings-organization",
    id: "api-keys",
    label: "API keys",
    settingsTab: "api-keys",
    to: "/settings/api-keys",
  },
  {
    group: "settings-organization",
    hostedOnly: true,
    id: "billing",
    label: "Billing",
    settingsTab: "billing",
    to: "/settings/billing",
  },
  {
    group: "settings-organization",
    id: "phone-numbers",
    label: "Phone numbers",
    settingsTab: "phone-numbers",
    to: "/settings/phone-numbers",
  },
  {
    group: "settings-organization",
    id: "retention",
    label: "Retention",
    settingsTab: "retention",
    to: "/settings/retention",
  },
  {
    group: "settings-organization",
    id: "audit-log",
    label: "Audit log",
    settingsTab: "audit-log",
    to: "/settings/audit-log",
  },
  // The org-wide activity page the sidebar usage row links to (docs/09 §4
  // Usage row): activity counters + breakdowns from metrics:query only.
  {
    group: "settings-organization",
    id: "usage",
    label: "Usage",
    settingsTab: "usage",
    to: "/settings/usage",
  },
  // The offline-license status page (docs/04-api-contract.md, "5a. License
  // status surface"; docs/08-on-prem-readiness.md, "XIV — Offline signed
  // licensing"): read-only state, term, and per-dimension usage. Self-managed
  // and air-gapped installs only — see `licensedOnly`. The ROUTE stays
  // registered everywhere, so a deep link still resolves and renders the
  // `not_required` view; only the nav row and palette entry are gated.
  {
    group: "settings-organization",
    id: "license",
    label: "License",
    licensedOnly: true,
    settingsTab: "license",
    to: "/settings/license",
  },
  // The Agent group's one row (docs/09 §4): bound to the working `?agent=`
  // scope. Palette label "Agent settings" (a bare "General" would be
  // ambiguous next to the Organization General row); the settings sidebar
  // shows "General" under its Agent header.
  {
    group: "settings-agent",
    id: "agent-settings",
    label: "Agent settings",
    settingsRowLabel: "General",
    settingsTab: "agent",
    to: "/settings/agent",
  },
  // No `appearance` entry: theme is the avatar menu's light / dark / system
  // ThemeControl (docs/09 §4) — there is no /settings/appearance route, live
  // or Soon.
  {
    group: "settings-personal",
    id: "profile",
    label: "Profile",
    settingsTab: "profile",
    to: "/settings/profile",
  },
];

// A registry entry that is live — has a concrete route and is not Soon. Lets
// `navigate({ to })` consume the path without a nullable type.
export type LiveShellNavEntry = ShellNavEntry & { to: ShellNavRoute };

// The settings shell's headed sections follow docs/09 §4:
// Organization → Team → Agent → Personal. Rows keep registry order within
// their group.
const SETTINGS_SECTIONS: ReadonlyArray<{
  group: ShellNavGroup;
  heading: string;
}> = [
  { group: "settings-organization", heading: "Organization" },
  { group: "settings-team", heading: "Team" },
  { group: "settings-agent", heading: "Agent" },
  { group: "settings-personal", heading: "Personal" },
];

export interface SettingsNavSection {
  entries: LiveShellNavEntry[];
  heading: string;
}

// Capability-gated rows surface only when the caller opts in: hosted-only
// rows on `includeHostedOnly` (GET /v1/me `credit_billing_enabled`), licensed
// rows on `includeLicensed` (GET /v1/system/license state != not_required),
// carrier rows on `includeNumberProvisioning`. Everything else is always
// visible. Every flag DEFAULTS TO FALSE — a gated row is hidden until its
// capability is positively known, so an install never flashes a row that its
// profile does not have.
export interface ShellNavVisibility {
  includeHostedOnly?: boolean;
  includeLicensed?: boolean;
  includeNumberProvisioning?: boolean;
}

function isVisibleEntry(
  entry: ShellNavEntry,
  {
    includeHostedOnly = false,
    includeLicensed = false,
    includeNumberProvisioning = false,
  }: ShellNavVisibility,
): boolean {
  if (entry.hostedOnly === true && !includeHostedOnly) {
    return false;
  }
  if (entry.licensedOnly === true && !includeLicensed) {
    return false;
  }
  return entry.numberProvisioningOnly !== true || includeNumberProvisioning;
}

// The settings-shell sidebar derives entirely from the registry, so a
// settings screen can never be live in chrome yet absent from its sidebar.
export function settingsNavSections(
  visibility: ShellNavVisibility = {},
): SettingsNavSection[] {
  return SETTINGS_SECTIONS.map(({ group, heading }) => ({
    entries: shellNavRegistry.filter(
      (entry): entry is LiveShellNavEntry =>
        entry.group === group &&
        entry.to !== undefined &&
        entry.soon !== true &&
        isVisibleEntry(entry, visibility),
    ),
    heading,
  }));
}

// The `/settings` index target (docs/09 §4): the agent row when the working
// scope has an agent, else the organization row. Pure so the redirect law is
// testable outside the router.
export function settingsIndexTarget(
  search: WorkingScopeSearch,
): "/settings/agent" | "/settings/organization" {
  return search.agent !== undefined && search.agent.length > 0
    ? "/settings/agent"
    : "/settings/organization";
}

// The command palette's Navigate group: every LIVE route in registry order,
// with Soon items excluded. Deriving here keeps the palette in lockstep with
// the rendered navigation.
export function liveShellNavEntries(
  visibility: ShellNavVisibility = {},
): LiveShellNavEntry[] {
  return shellNavRegistry.filter(
    (entry): entry is LiveShellNavEntry =>
      entry.to !== undefined &&
      entry.soon !== true &&
      isVisibleEntry(entry, visibility),
  );
}

export function isAgentScopeWiredArea(area: ShellArea): boolean {
  return agentScopeWiredAreaSet.has(area);
}

function isTeamBoundScopeRoute(to: ShellNavRoute): boolean {
  return teamBoundScopeRoutes.has(to);
}

export interface ShellRouteState {
  agentId?: string;
  authoring?: "variables";
  alertAction?: "create" | "silence";
  alertRuleId?: string;
  area: ShellArea;
  dashboardId?: string;
  markerAction?: "create" | "edit";
  settingsTab?: ShellSettingsTab;
  simulationAction?: "create" | "templateCreate" | "templateEdit";
  templateId?: string;
  scenarioId?: string;
  teamId?: string;
  transcriptId?: string;
}

export type CoordinatedAgentSwitchDestination =
  | {
      search: WorkingScopeSearch;
      to: "/scenarios";
    }
  | {
      params: { agentId: string };
      search: WorkingScopeSearch;
      to: "/agents/$agentId";
    }
  | {
      params: { agentId: string };
      search: WorkingScopeSearch;
      to: "/agents/$agentId/variables";
    };

export interface CoordinatedTeamSwitchDestination {
  search: { team: string };
  to: "/scenarios";
}

// Agent-detail and scenario-row authoring routes cannot be re-scoped in
// place. The former changes its path owner; the latter exits to the Scenarios
// list under the new canonical scope so the old scenario never remains
// mounted beneath a different agent selector.
export function coordinatedAgentSwitchDestination(
  routeState: ShellRouteState,
  nextAgentId: string,
): CoordinatedAgentSwitchDestination | undefined {
  if (routeState.scenarioId !== undefined) {
    return { search: { agent: nextAgentId }, to: "/scenarios" };
  }
  if (routeState.authoring === "variables") {
    return {
      params: { agentId: nextAgentId },
      search: {},
      to: "/agents/$agentId/variables",
    };
  }
  if (routeState.area === "agent" && routeState.agentId !== undefined) {
    return {
      params: { agentId: nextAgentId },
      search: {},
      to: "/agents/$agentId",
    };
  }
  return undefined;
}

export function coordinatedTeamSwitchDestination(
  routeState: ShellRouteState,
  nextTeamId: string,
): CoordinatedTeamSwitchDestination | undefined {
  if (routeState.scenarioId !== undefined) {
    return { search: { team: nextTeamId }, to: "/scenarios" };
  }
  return undefined;
}

function normalizePathname(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed.length > 0 ? trimmed : "/";
}

// The functional search updater for every chrome navigation (sidebar rail,
// organization menu, command palette, profile shortcut). Two laws
// (docs/09 §4, shell v2):
//
// 1. Agent scope is canonical agent-only: product and agent-bound settings
//    destinations carry `?agent=` without duplicating the parent `?team=`.
//    Team scope is preserved for All agents and for team-bound settings rows.
//    A direct `/agents/$agentId` deep link projects that agent into
//    scope-wired destinations.
// 2. Screen-local params (`?new`, `?rule`, `?tab`, ...) never leak across
//    routes: when the destination route differs from the current route, ONLY
//    the root-owned keys pass through (memberships-settings.md chrome-leak
//    fix — `/settings/members?new=1` must not open the invitations drawer).
//    Same-route navigation keeps the owning screen's params.
export function shellNavigationSearch(
  to: ShellNavRoute,
  currentPathname: string,
  routeAgentId?: string,
  resolvedTeamId?: string,
) {
  return (prev: WorkingScopeSearch): WorkingScopeSearch => {
    const isSameRoute =
      normalizePathname(currentPathname) === normalizePathname(to);
    const destinationArea = parseShellPath(to).area;
    const projectsRouteAgent =
      routeAgentId !== undefined && isAgentScopeWiredArea(destinationArea);
    const agent = projectsRouteAgent ? routeAgentId : prev.agent;
    const team =
      agent !== undefined && !isTeamBoundScopeRoute(to)
        ? undefined
        : (prev.team ?? resolvedTeamId);

    if (isSameRoute) {
      if (projectsRouteAgent) {
        return { ...prev, agent, team };
      }
      return prev;
    }

    const next: WorkingScopeSearch = {};
    if (team !== undefined) {
      next.team = team;
    }
    if (agent !== undefined) {
      next.agent = agent;
    }
    return next;
  };
}

// Pure pathname → shell area. The usual active agent scope rides a `?agent=`
// search param, but `/agents/$agentId` remains a direct Agent-page deep link.
export function parseShellPath(pathname: string): ShellRouteState {
  const transcriptMatch = /^\/transcripts\/([^/]+)\/?$/.exec(pathname);
  const transcriptId = transcriptMatch?.[1];
  if (transcriptId) {
    return { area: "transcripts", transcriptId };
  }

  if (pathname === "/transcripts") {
    return { area: "transcripts" };
  }

  if (pathname === "/markers/new") {
    return { area: "markers", markerAction: "create" };
  }

  // The Authoring-archetype marker edit route (markers.md, sweep Phase 2):
  // `/markers/$markerId/edit` stays in the markers area.
  if (/^\/markers\/[^/]+\/edit\/?$/.test(pathname)) {
    return { area: "markers", markerAction: "edit" };
  }

  if (pathname === "/markers") {
    return { area: "markers" };
  }

  if (pathname === "/agent") {
    return { area: "agent" };
  }

  // The agents index (docs/09 §4): exact `/agents` only — an id segment is
  // the Agent-page deep link below.
  if (pathname === "/agents") {
    return { area: "agents" };
  }

  if (pathname === "/personas") {
    return { area: "personas" };
  }

  const variablesMatch = /^\/agents\/([^/]+)\/variables\/?$/.exec(pathname);
  if (variablesMatch?.[1]) {
    return {
      agentId: variablesMatch[1],
      area: "agent",
      authoring: "variables",
    };
  }

  const scenarioRowsMatch = /^\/scenarios\/([^/]+)\/rows\/?$/.exec(pathname);
  if (scenarioRowsMatch?.[1]) {
    return { area: "scenarios", scenarioId: scenarioRowsMatch[1] };
  }

  // Scenario authoring is top-level and agent-scoped (founder ratification
  // 2026-07-31); scenario sets are its second tab, so both share one area.
  if (pathname === "/scenarios") {
    return { area: "scenarios" };
  }

  // The Simulations launch hub + its Authoring-archetype routes (docs/09 §4):
  // the launch form and both template authoring routes stay in the
  // simulations area — a Template is a saved launch spec, not its own surface.
  if (pathname === "/simulations/new") {
    return { area: "simulations", simulationAction: "create" };
  }

  if (pathname === "/simulations/templates/new") {
    return { area: "simulations", simulationAction: "templateCreate" };
  }

  const templateEditMatch = /^\/simulations\/templates\/([^/]+)\/?$/.exec(
    pathname,
  );
  if (templateEditMatch?.[1]) {
    return {
      area: "simulations",
      simulationAction: "templateEdit",
      templateId: templateEditMatch[1],
    };
  }

  if (pathname === "/simulations") {
    return { area: "simulations" };
  }

  const agentMatch = /^\/agents\/([^/]+)\/?$/.exec(pathname);
  const agentId = agentMatch?.[1];
  if (agentId) {
    return { agentId, area: "agent" };
  }

  const dashboardMatch = /^\/dashboard\/([^/]+)\/?$/.exec(pathname);
  const dashboardId = dashboardMatch?.[1];
  if (dashboardId) {
    return { area: "dashboard", dashboardId };
  }

  if (pathname === "/dashboard") {
    return { area: "dashboard" };
  }

  if (pathname === "/review" || pathname.startsWith("/review/")) {
    return { area: "review" };
  }

  if (pathname === "/alerts/rules/new") {
    return { alertAction: "create", area: "alerts" };
  }

  const alertSilenceMatch = /^\/alerts\/rules\/([^/]+)\/silence\/?$/.exec(
    pathname,
  );
  if (alertSilenceMatch?.[1]) {
    return {
      alertAction: "silence",
      alertRuleId: alertSilenceMatch[1],
      area: "alerts",
    };
  }

  if (pathname === "/alerts") {
    return { area: "alerts" };
  }

  // The settings-shell front door: the index route redirects before render
  // (settingsIndexTarget), but chrome still needs the area for the rail's
  // active state.
  if (pathname === "/settings") {
    return { area: "settings" };
  }

  if (pathname === "/settings/agent") {
    return { area: "settings", settingsTab: "agent" };
  }

  if (pathname === "/settings/api-keys") {
    return { area: "settings", settingsTab: "api-keys" };
  }

  if (pathname === "/settings/audit-log") {
    return { area: "settings", settingsTab: "audit-log" };
  }

  if (pathname === "/settings/billing") {
    return { area: "settings", settingsTab: "billing" };
  }

  if (pathname === "/settings/phone-numbers") {
    return { area: "settings", settingsTab: "phone-numbers" };
  }

  if (pathname === "/settings/organization") {
    return { area: "settings", settingsTab: "organization" };
  }

  if (pathname === "/settings/profile") {
    return { area: "settings", settingsTab: "profile" };
  }

  if (pathname === "/settings/retention") {
    return { area: "settings", settingsTab: "retention" };
  }

  if (pathname === "/settings/usage") {
    return { area: "settings", settingsTab: "usage" };
  }

  if (pathname === "/settings/license") {
    return { area: "settings", settingsTab: "license" };
  }

  const teamSettingsDetailMatch = /^\/settings\/teams\/([^/]+)\/?$/.exec(
    pathname,
  );
  const teamId = teamSettingsDetailMatch?.[1];
  if (teamId) {
    return { area: "settings", settingsTab: "teams", teamId };
  }

  if (pathname === "/settings/members") {
    return { area: "settings", settingsTab: "members" };
  }

  if (pathname === "/settings/invitations") {
    return { area: "settings", settingsTab: "invitations" };
  }

  if (pathname === "/settings/teams") {
    return { area: "settings", settingsTab: "teams" };
  }

  return { area: "transcripts" };
}

export function shortResourceId(id: string): string {
  if (id.length <= 10) {
    return id;
  }

  return `${id.slice(0, 4)}...${id.slice(-4)}`;
}

export function initials(value: string): string {
  const words = value.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? "M";
  const second = words[1]?.[0] ?? words[0]?.[1] ?? "";

  return `${first}${second}`.toUpperCase();
}

export interface ShellScopeInput {
  isPending: boolean;
  // Authoritative org from /v1/me. No team input: the session has no
  // active-team concept, so the shell renders org-only.
  organizationName?: string | null;
}

export interface ShellScopeSummary {
  organizationInitials: string;
  organizationName: string;
}

const LOADING_ORGANIZATION = "Loading organization";
const FALLBACK_ORGANIZATION = "Current organization";

// Derive the scope-header summary from the resolved session. Truthful over
// "stub": while /v1/me is pending we show a loading label; once resolved we
// show the real org name, falling back to a neutral placeholder only when the
// name is truly absent. No fabricated "Stub" badge and no team row — showing
// an arbitrary /v1/teams row as the current team would be untruthful.
export function buildScopeSummary(input: ShellScopeInput): ShellScopeSummary {
  if (input.isPending) {
    return {
      organizationInitials: initials(LOADING_ORGANIZATION),
      organizationName: LOADING_ORGANIZATION,
    };
  }

  const organizationName = input.organizationName?.trim()
    ? input.organizationName
    : FALLBACK_ORGANIZATION;

  return {
    organizationInitials: initials(organizationName),
    organizationName,
  };
}
